import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import {
  useCreateSession,
  useDeleteSession,
  useInvalidateSession,
  useUpdateSession,
  useUpdateWeatherData,
  useSessions,
  useDuplicateSession,
} from '../../../hooks/useSessions';
import { useRidersById } from '../../../hooks/useRiders';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@mui/material';
import {
  HiOutlineTrash,
  HiOutlineCheckCircle,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineXMark,
  HiOutlinePencil,
  HiPlus,
  HiChevronLeft,
  HiChevronRight,
  HiDocumentDuplicate,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2';
import { useQueryClient } from '@tanstack/react-query';
import { ImSpinner2 } from 'react-icons/im';
import toast from 'react-hot-toast';
import { SessionAnalysisType, SessionCatalog, SessionStatus } from '../../../types/SessionAnalysisType';
import Button from '../../../components/Button';
import ButtonList from '../../../components/ButtonList';
import FormInput from '../../../components/FormInput';
import FormEntry from '../../../components/FormEntry';
import Skeleton from '../../../components/Skeleton';
// Combined component with radio button selection
import NewSessionCalibrationParameters from './NewSessionParameters';
import NewSessionCalibrationConfig from './NewSessionCalibrationConfig';
import NewSessionRouteSelector from './NewSessionRouteSelector';
import AccordionIcon from './SessionConfigs/AccordionIcon';
import { useActionGuard } from '../../../components/ActionGuard/useActionGuard';
import SectionHeader from '../../../components/SectionHeader';
import DetailCard from '../../../components/DetailCard';
import { useDebouncedSave } from 'hooks/useDebounce';
import { format, parse, isValid } from 'date-fns';
import ParentSessionSelector from '../../../components/ParentSessionSelector';
import WeatherTemperaturePlot from './WeatherTemperaturePlot';
import { SurfaceQuality, SurfaceEnum } from '../../../types/SurfaceQuality';
import VelodromeRouteSelector from '../../../components/VelodromeRouteSelector';
import { compareSessionsAsc } from '../../../utils/sessionSort';
import { getSessionRunTimeRange } from '../../../utils/sessionTime';
import { useSessionColor, SESSION_COLORS } from '../../../hooks/useSessionColor';

interface SessionFormProps {
  sessionId: string;
  session: any;
  isSessionFetching: boolean;
  isSessionPending: boolean;
  isNew: boolean;
}

const SessionDate = ({
  formMethods,
  save,
  isNew,
  setIsSaving,
}: {
  formMethods: any;
  save: (update: any) => Promise<void>;
  isNew: boolean;
  setIsSaving: (isSaving: boolean) => void;
}) => {
  useDebouncedSave('date', async (value: string) => save({ date: value }), {
    setIsSaving: setIsSaving,
    enabled: !isNew,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/*{ formMethods.getValues('date')?.toString() }*/}
      <Controller
        name="date"
        control={formMethods.control}
        rules={{
          required: 'Date is required',
          validate: (value) => {
            if (!value) {
              return 'Date is required';
            }
            return true;
          },
        }}
        render={({ field }) => (
          <DatePicker
            value={parse(field?.value, 'yyyy-MM-dd', new Date())}
            onChange={async (newValue) => {
              const dateOnly = newValue && isValid(newValue) ? format(newValue, 'yyyy-MM-dd') : null;
              field.onChange(dateOnly);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                required
                error={!!formMethods.formState.errors.date}
                helperText={formMethods.formState.errors.date?.message || ''}
                className="!cr-mt-0 cr-w-40 cr-rounded-md"
                InputProps={params.InputProps}
                sx={{
                  '& .MuiInputBase-root': {
                    marginTop: 0,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '16px',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': formMethods.formState.errors
                    .date
                    ? {
                        borderColor: '#ef4444 !important', // Tailwind red-500
                        borderWidth: '2px',
                      }
                    : {
                        borderColor: '#03365D !important',
                        borderWidth: '2px',
                      },
                  '& .MuiFormHelperText-root.Mui-error': {
                    color: '#ef4444',
                  },

                  marginTop: 0,
                }}
              />
            )}
          />
        )}
      />
    </LocalizationProvider>
  );
};

// Move SessionCards outside of SessionForm to prevent recreation
const SessionCards = React.memo(
  ({
    formMethods,
    ridersById,
    getRiderName,
    isNew,
    updateSession,
    showRiderWarning,
    AdvancedOptionsComponent,
    session,
    onSummaryUpdate,
  }: {
    formMethods: any;
    ridersById: any;
    getRiderName: (rider_id: string) => string;
    isNew: boolean;
    updateSession: any;
    showRiderWarning?: boolean;
    AdvancedOptionsComponent?: React.ComponentType;
    session?: any;
    onSummaryUpdate?: () => Promise<void>;
  }) => {
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [summaryContent, setSummaryContent] = useState('');
    const [isSavingSummary, setIsSavingSummary] = useState(false);

    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [notesContent, setNotesContent] = useState('');
    const [isSavingNotes, setIsSavingNotes] = useState(false);

    // Convert <br> tags to newlines for editing
    const htmlToTextarea = (html: string) => html.replace(/<br\s*\/?>/gi, '\n');

    // Update summaryContent when session changes (only when not editing)
    React.useEffect(() => {
      if (!isEditingSummary) {
        setSummaryContent(htmlToTextarea(session?.summary || ''));
      }
    }, [session?.summary, isEditingSummary]);

    // Update notesContent when session changes (only when not editing)
    React.useEffect(() => {
      if (!isEditingNotes) {
        setNotesContent(htmlToTextarea(session?.notes || ''));
      }
    }, [session?.notes, isEditingNotes]);

    const save = async (update: any) => {
      await updateSession.mutateAsync(update);
    };

    const hasSummary = session?.summary && session.summary.length > 0;
    const hasNotes = session?.notes && session.notes.length > 0;

    const handleSaveSummary = async () => {
      setIsSavingSummary(true);
      try {
        // Convert newlines to <br> tags for HTML display
        const formattedContent = summaryContent.replace(/\r?\n/g, '<br>');
        await save({ summary: formattedContent });
        if (onSummaryUpdate) {
          await onSummaryUpdate();
        }
        setIsEditingSummary(false);
      } catch (e) {
        console.error(e);
      } finally {
        setIsSavingSummary(false);
      }
    };

    const handleCancelSummary = () => {
      setSummaryContent(htmlToTextarea(session?.summary || ''));
      setIsEditingSummary(false);
    };

    const handleSaveNotes = async () => {
      setIsSavingNotes(true);
      try {
        // Convert newlines to <br> tags for HTML display
        const formattedContent = notesContent.replace(/\r?\n/g, '<br>');
        await save({ notes: formattedContent });
        if (onSummaryUpdate) {
          await onSummaryUpdate();
        }
        setIsEditingNotes(false);
      } catch (e) {
        console.error(e);
      } finally {
        setIsSavingNotes(false);
      }
    };

    const handleCancelNotes = () => {
      setNotesContent(htmlToTextarea(session?.notes || ''));
      setIsEditingNotes(false);
    };

    return (
      <div className="cr-flex cr-flex-col cr-gap-0 cr-pb-4">
        {/* Cards container — wrapper shrinks to content width on md+ so child cards can match */}
        <div className="md:cr-w-max">
        <div className="cr-grid cr-grid-cols-2 cr-gap-4 md:cr-flex md:cr-flex-row md:cr-gap-4">
          <DetailCard key="date-card" label="Date" canEdit={!isNew}>
            {(isEdit, setIsSaving) =>
              isEdit || isNew ? (
                <SessionDate formMethods={formMethods} save={save} isNew={isNew} setIsSaving={setIsSaving} />
              ) : (
                parse(formMethods.getValues('date'), 'yyyy-MM-dd', new Date()).toLocaleDateString()
              )
            }
          </DetailCard>
          <DetailCard
            key="rider-card"
            label="Rider"
            canEdit={!isNew}
            className={showRiderWarning ? 'cr-animate-pulse cr-ring-2 cr-ring-orange-400 cr-ring-offset-2' : ''}
          >
            {(isEdit, setIsSaving) =>
              isEdit || isNew ? (
                <FormInput
                  fieldName="rider_id"
                  required="Rider is required"
                  options={ridersById && Array.from(ridersById.values()).filter(
                    (rider: any) => !rider.non_current || rider.id === formMethods.getValues('rider_id'),
                  )}
                  optionLabelFn={(rider: any) => rider.nickname}
                  emptyOption={isNew ? 'Select a Rider' : undefined}
                  customClass="!cr-h-[42px] !cr-w-44 cr-mx-auto"
                  enableDebouncedSave={!isNew}
                  debouncedSaveFn={async (value: string) => {
                    await save({ rider_id: value });
                  }}
                  setIsSaving={setIsSaving}
                />
              ) : (
                getRiderName(formMethods.getValues('rider_id'))
              )
            }
          </DetailCard>
          <DetailCard
            key="description-card"
            label="Description"
            canEdit={true}
            className="cr-col-span-2 md:cr-col-span-1"
          >
            {(isEdit, setIsSaving) =>
              isEdit || isNew ? (
                <FormInput
                  fieldName="description"
                  required="Description is required"
                  rows={2}
                  defaultValue="Test Session"
                  customClass="!cr-h-[42px] !cr-w-44 cr-mx-auto"
                  enableDebouncedSave={!isNew}
                  debouncedSaveFn={async (value: string) => {
                    await save({ description: value });
                  }}
                  setIsSaving={setIsSaving}
                />
              ) : (
                formMethods.getValues('description')
              )
            }
          </DetailCard>
        </div>
        {!isNew && (() => {
          const range = getSessionRunTimeRange(session);
          const analysisLabel = SessionCatalog[session?.analysis_type as SessionAnalysisType]?.[0];
          const cardClass =
            'cr-mt-2 cr-w-full cr-rounded-lg cr-bg-white cr-px-3 cr-py-2 ' +
            'cr-shadow-md cr-text-sm cr-italic cr-font-main';
          return (
            <div className={`${cardClass} cr-text-gray-700`}>
              Runs:{' '}
              {range ? (
                <>
                  {range.start} - {range.end}
                  {range.tzLabel && (
                    <span className="cr-ml-1 cr-text-gray-500">({range.tzLabel})</span>
                  )}
                </>
              ) : (
                <span className="cr-text-gray-500">Planned</span>
              )}
              {analysisLabel && (
                <span className="cr-ml-2 cr-text-gray-500">• {analysisLabel}</span>
              )}
            </div>
          );
        })()}
        </div>

        {/* Summary card - only show if summary exists and has content */}
        {!isNew && hasSummary && (
          <div className="cr-mt-4">
            <div
              className={`cr-group cr-relative cr-w-full cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md ${!isEditingSummary ? 'cr-cursor-pointer' : ''}`}
              onClick={() => !isEditingSummary && setIsEditingSummary(true)}
            >
              {!isEditingSummary && (
                <div className="cr-absolute cr-right-2 cr-top-2 cr-opacity-0 cr-transition-opacity cr-duration-200 group-hover:cr-opacity-100">
                  <HiOutlinePencil className="cr-text-xl cr-text-blue-900" />
                </div>
              )}
              <label className="font-montserrat cr-mb-2 cr-block cr-text-lg cr-font-bold cr-uppercase cr-text-blue-900">
                Summary
              </label>
              {isEditingSummary ? (
                <div className="cr-flex cr-flex-col cr-gap-2">
                  <textarea
                    className="cr-min-h-[150px] cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-2 cr-font-main cr-text-sm cr-text-blue-900 focus:cr-border-blue-500 focus:cr-outline-none"
                    value={summaryContent}
                    onChange={(e) => setSummaryContent(e.target.value)}
                    placeholder="Enter summary content (HTML supported)..."
                  />
                  <div className="cr-flex cr-justify-end cr-gap-2">
                    <button
                      onClick={handleCancelSummary}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-300"
                    >
                      <HiOutlineXMark className="cr-h-4 cr-w-4" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveSummary}
                      disabled={isSavingSummary}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-blue-900 cr-px-3 cr-py-1.5 cr-text-sm cr-text-white hover:cr-bg-blue-850 disabled:cr-opacity-50"
                    >
                      <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />
                      {isSavingSummary ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="cr-font-main cr-text-sm cr-text-blue-900"
                  dangerouslySetInnerHTML={{ __html: session?.summary || '' }}
                />
              )}
            </div>
          </div>
        )}

        {/* Notes card - only show if notes exists and has content */}
        {!isNew && hasNotes && (
          <div className="cr-mt-4">
            <div
              className={`cr-group cr-relative cr-w-full cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md ${!isEditingNotes ? 'cr-cursor-pointer' : ''}`}
              onClick={() => !isEditingNotes && setIsEditingNotes(true)}
            >
              {!isEditingNotes && (
                <div className="cr-absolute cr-right-2 cr-top-2 cr-opacity-0 cr-transition-opacity cr-duration-200 group-hover:cr-opacity-100">
                  <HiOutlinePencil className="cr-text-xl cr-text-blue-900" />
                </div>
              )}
              <label className="font-montserrat cr-mb-2 cr-block cr-text-lg cr-font-bold cr-uppercase cr-text-blue-900">
                Notes
              </label>
              {isEditingNotes ? (
                <div className="cr-flex cr-flex-col cr-gap-2">
                  <textarea
                    className="cr-min-h-[150px] cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-2 cr-font-main cr-text-sm cr-text-blue-900 focus:cr-border-blue-500 focus:cr-outline-none"
                    value={notesContent}
                    onChange={(e) => setNotesContent(e.target.value)}
                    placeholder="Enter notes content (HTML supported)..."
                  />
                  <div className="cr-flex cr-justify-end cr-gap-2">
                    <button
                      onClick={handleCancelNotes}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-300"
                    >
                      <HiOutlineXMark className="cr-h-4 cr-w-4" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveNotes}
                      disabled={isSavingNotes}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-blue-900 cr-px-3 cr-py-1.5 cr-text-sm cr-text-white hover:cr-bg-blue-850 disabled:cr-opacity-50"
                    >
                      <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />
                      {isSavingNotes ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="cr-font-main cr-text-sm cr-text-blue-900"
                  dangerouslySetInnerHTML={{ __html: session?.notes || '' }}
                />
              )}
            </div>
          </div>
        )}

        {/* Options section - show below cards on both mobile and desktop */}
        {!isNew && AdvancedOptionsComponent && (
          <div>
            <AdvancedOptionsComponent />
          </div>
        )}
      </div>
    );
  },
);
SessionCards.displayName = 'SessionCards';

export default function SessionForm({
  sessionId,
  session,
  isSessionFetching,
  isSessionPending,
  isNew,
}: SessionFormProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const deleteSession = useDeleteSession();
  const invalidateSession = useInvalidateSession(sessionId);
  const updateWeatherData = useUpdateWeatherData(sessionId);
  const { data: ridersById, isPending: isRidersPending } = useRidersById();
  const { data: allSessions } = useSessions();

  // Local state management
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showChangeSessionType, setShowChangeSessionType] = useState(false);
  const [showMeteorologicalSource, setShowMeteorologicalSource] = useState(false);
  const [showRiderWarning, setShowRiderWarning] = useState(false);
  const [showTestTypeWarning, setShowTestTypeWarning] = useState(false);
  const [showVelodromeWarning, setShowVelodromeWarning] = useState(false);
  const [sessionTypeChanged, setSessionTypeChanged] = useState(false);
  const [isSavingSessionType, setIsSavingSessionType] = useState(false);
  const [meteorologicalSource, setMeteorologicalSource] = useState<string>('Weather Api');
  const [meteorologicalSourceChanged, setMeteorologicalSourceChanged] = useState(false);
  const [isUpdatingWeatherSource, setIsUpdatingWeatherSource] = useState(false);
  const [expandedWeatherEdit, setExpandedWeatherEdit] = useState<string | null>(null);
  const [isUpdatingProbeOffset, setIsUpdatingProbeOffset] = useState(false);
  const [isUpdatingManualWeather, setIsUpdatingManualWeather] = useState(false);
  const [editingWeatherRow, setEditingWeatherRow] = useState<number | null>(null);
  const [showSurfaceQuality, setShowSurfaceQuality] = useState(false);
  const [surfaceQualityObj, setSurfaceQualityObj] = useState<SurfaceQuality>(SurfaceQuality.fromFloat(10.25));
  const [surfaceQualityChanged, setSurfaceQualityChanged] = useState(false);
  const [isUpdatingSurfaceQuality, setIsUpdatingSurfaceQuality] = useState(false);
  const [sessionColor, setSessionColor] = useSessionColor(sessionId);
  const [showSessionColor, setShowSessionColor] = useState(false);
  const [showSessionDuplicate, setShowSessionDuplicate] = useState(false);
  const duplicateSessionMutation = useDuplicateSession();
  const [sessionMode, setSessionModeState] = useState<'basic' | 'advanced'>(
    () => {
      const stored = sessionStorage.getItem('sessionMode');
      return stored === 'advanced' ? 'advanced' : 'basic';
    },
  );
  const setSessionMode = (mode: 'basic' | 'advanced') => {
    sessionStorage.setItem('sessionMode', mode);
    setSessionModeState(mode);
  };
  const isMissingConfigs = !session || !session.configs || session.configs.length === 0;

  const sessionBasePath = `/sessions/${sessionId}`;
  const configsPath = `${sessionBasePath}/config`;
  const onConfigsView = location.pathname === configsPath || location.pathname === sessionBasePath;
  const headerBackLink =
    !onConfigsView && !isNew
      ? { to: configsPath, text: 'Back to Configs' }
      : { to: '/sessions', text: 'Back to Sessions' };

  // Calculate next and previous sessions
  const { nextSessionId, previousSessionId } = useMemo(() => {
    if (!allSessions || !sessionId || isNew) {
      return { nextSessionId: null, previousSessionId: null };
    }

    const sortedSessions = [...allSessions].sort((a, b) => compareSessionsAsc(a, b, ridersById));

    const currentIndex = sortedSessions.findIndex((s) => s.id === sessionId);

    if (currentIndex === -1) {
      return { nextSessionId: null, previousSessionId: null };
    }

    const previousSessionId = currentIndex > 0 ? sortedSessions[currentIndex - 1].id : null;
    const nextSessionId = currentIndex < sortedSessions.length - 1 ? sortedSessions[currentIndex + 1].id : null;

    return { nextSessionId, previousSessionId };
  }, [allSessions, sessionId, isNew, ridersById]);

  const showBikeComputerOption = Boolean(
    session?.weather_controller_dict?.bike_computer ||
      session?.weather_controller_dict?.selection === 'bike_computer' ||
      session?.meteorological_source === 'Bike Computer' ||
      meteorologicalSource === 'Bike Computer',
  );

  const meteorologicalSourceSelectId = 'meteorological-source-select';

  const manualWeatherRows = useMemo(() => {
    const manualData = session?.weather_controller_dict?.manual?.weather_data;
    if (!manualData) {
      return [];
    }

    const rows = Array.isArray(manualData) ? manualData : [manualData];
    return [...rows].sort((a: any, b: any) => (a?.TimeAbs_t ?? 0) - (b?.TimeAbs_t ?? 0));
  }, [session]);

  const sessionProbeOffset = session?.weather_controller_dict?.offsets_raw?.probe?.Temp_T;

  // Form methods
  const formMethods = useForm({
    defaultValues: {
      ...session,
      date: session?.date || new Date().toISOString().split('T')[0],
      analysis_type: session?.analysis_type || (isNew ? SessionAnalysisType.TRAVERSE : session?.analysis_type),
    },
  });

  // Set Basic mode defaults when in basic mode for new sessions
  useEffect(() => {
    if (sessionMode === 'basic' && isNew) {
      formMethods.setValue(
        'analysis_type',
        SessionAnalysisType.TRAVERSE,
        { shouldValidate: true },
      );
      // Basic mode is a rider TRAVERSE test: pin config A head/chest slopes manual-0 so
      // the drag-model solve holds body position at 0 (matches the rider radio default).
      formMethods.setValue('head_slope', 0, { shouldValidate: true });
      formMethods.setValue('chest_slope', 0, { shouldValidate: true });
      formMethods.setValue(
        'front_tooth_count', 56, { shouldValidate: true },
      );
      setShowTestTypeWarning(false);
      // Basic mode is always a TRAVERSE session, so the velodrome gate never applies.
      setShowVelodromeWarning(false);
    }
  }, [sessionMode, isNew, formMethods]);

  // Watch for analysis_type changes to show/hide parent session dropdown
  const watchedAnalysisType = formMethods.watch('analysis_type');
  const currentAnalysisType = Number(watchedAnalysisType || session?.analysis_type);

  const showParentSessionDropdown = currentAnalysisType === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  // Check if velodrome type is selected
  const isVelodromeSession =
    currentAnalysisType === SessionAnalysisType.LOOP_VELODROME_INDOORS ||
    currentAnalysisType === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;

  // Handle meteorological source change
  const handleMeteorologicalSourceChange = (newSource: string) => {
    setMeteorologicalSource(newSource);

    // Check if the selection has changed from the original
    const backendKeyMap: Record<string, string> = {
      'Weather Api': 'weather_api',
      Probe: 'probe',
      'Probe-API': 'probe_hybrid',
      'Local Weather Station': 'local_station',
      Manual: 'manual',
      'Bike Computer': 'bike_computer',
    };

    const newBackendKey = backendKeyMap[newSource];
    const currentBackendKey = session?.weather_controller_dict?.selection;

    setMeteorologicalSourceChanged(newBackendKey !== currentBackendKey);
  };

  // Handle updating the weather source selection
  const handleUpdateWeatherSource = async () => {
    const backendKeyMap: Record<string, string> = {
      'Weather Api': 'weather_api',
      Probe: 'probe',
      'Probe-API': 'probe_hybrid',
      'Local Weather Station': 'local_station',
      Manual: 'manual',
      'Bike Computer': 'bike_computer',
    };

    const backendKey = backendKeyMap[meteorologicalSource];

    try {
      setIsUpdatingWeatherSource(true);
      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...session.weather_controller_dict,
          selection: backendKey,
        },
      });
      toast.success('Weather source updated');
      setMeteorologicalSourceChanged(false);
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating weather source');
    } finally {
      setIsUpdatingWeatherSource(false);
    }
  };

  // Handle updating Weather Api data
  const handleUpdateWeatherApi = async () => {
    try {
      await updateWeatherData.mutateAsync();
      toast.success('Weather API data updated');
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating weather API data');
    }
  };

  // Handle updating probe temperature offset
  const handleUpdateProbeOffset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!session) {
      toast.error('Session data not available');
      return;
    }

    // Get form data
    const formData = new FormData(e.currentTarget);
    const offsetValue = ((formData.get('probeOffset') as string) || '').trim();

    // Parse and validate offset input
    if (offsetValue === '' || offsetValue === '-' || offsetValue === '.') {
      toast.error('Temperature offset is required');
      return;
    }
    if (!/^-?\d+(\.\d+)?$/.test(offsetValue)) {
      toast.error('Temperature offset must be a valid number');
      return;
    }
    const offset = Number(offsetValue);
    if (Number.isNaN(offset)) {
      toast.error('Temperature offset must be a valid number');
      return;
    }

    try {
      setIsUpdatingProbeOffset(true);

      // Create updated offsets_raw object
      const updatedOffsetsRaw = {
        ...session.weather_controller_dict.offsets_raw,
        probe: {
          ...session.weather_controller_dict.offsets_raw?.probe,
          Temp_T: offset,
        },
      };

      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...session.weather_controller_dict,
          offsets_raw: updatedOffsetsRaw,
        },
      });

      toast.success('Probe temperature offset updated');
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating probe temperature offset');
    } finally {
      setIsUpdatingProbeOffset(false);
    }
  };

  // Handle updating manual weather data
  const handleSaveManualWeather = async (hour: number, minute: number, temperature: number, humidity: number) => {
    if (!session) {
      toast.error('Session data not available');
      return;
    }

    try {
      setIsUpdatingManualWeather(true);

      // Interpret the entered hour:minute as the recording-location wall clock
      // (matching the weather plots, which shift by session.utc_offset), then
      // convert to a UTC epoch for storage by removing that offset. Date.UTC is
      // used so the viewer's browser zone never enters the calculation.
      const sessionDate = session?.date || new Date().toISOString().split('T')[0];
      const [yearStr, monthStr, dayStr] = sessionDate.split('-');
      const utcOffsetSeconds = Number(session?.utc_offset ?? 0);
      const wallClockMs = Date.UTC(Number(yearStr), Number(monthStr) - 1, Number(dayStr), hour, minute, 0);
      const TimeAbs_t = Math.floor(wallClockMs / 1000) - utcOffsetSeconds;

      // Create the manual weather data point
      const manualWeatherData = {
        TimeAbs_t: TimeAbs_t,
        Temp_T: temperature,
        Baro_P: null,
        Humidity_x: humidity,
        WindDir_A: null,
        Wind_v: null,
      };

      // Get existing manual data or create new array
      const existingManualData = manualWeatherRows.map((row: any) => ({ ...row }));

      // When editing, remove the entry being edited (by editingWeatherRow timestamp)
      // When adding new (-1), just filter out any duplicate at the new timestamp
      const timestampToRemove = editingWeatherRow === -1 ? manualWeatherData.TimeAbs_t : editingWeatherRow;
      const dedupedManualData = existingManualData.filter((row: any) => row?.TimeAbs_t !== timestampToRemove);

      // If editing and time changed, also remove any entry at the new timestamp to prevent duplicates
      const finalManualData =
        editingWeatherRow !== -1 && editingWeatherRow !== manualWeatherData.TimeAbs_t
          ? dedupedManualData.filter((row: any) => row?.TimeAbs_t !== manualWeatherData.TimeAbs_t)
          : dedupedManualData;

      const updatedManualData = [...finalManualData, manualWeatherData].sort((a, b) => a.TimeAbs_t - b.TimeAbs_t);

      // Create manual object
      const manualObject = {
        ...(session.weather_controller_dict?.manual || {}),
        ts: Math.floor(Date.now() / 1000),
        weather_data: updatedManualData,
      };

      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...(session.weather_controller_dict || {}),
          manual: manualObject,
        },
      });

      toast.success('Manual weather data saved');
      // Clear editing state
      setEditingWeatherRow(null);
      await invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error adding manual weather data');
    } finally {
      setIsUpdatingManualWeather(false);
    }
  };

  const handleEditManualWeatherRow = (row: any) => {
    setEditingWeatherRow(row?.TimeAbs_t);
  };

  const handleCancelEdit = () => {
    setEditingWeatherRow(null);
  };

  const handleAddNewRow = () => {
    setEditingWeatherRow(-1); // Use -1 to indicate new row
  };

  const handleRemoveManualWeatherRow = async (timeAbs: number) => {
    if (!session) {
      toast.error('Session data not available');
      return;
    }

    const filteredRows = manualWeatherRows.filter((row: any) => row?.TimeAbs_t !== timeAbs);

    if (filteredRows.length === manualWeatherRows.length) {
      toast.error('Manual entry not found');
      return;
    }

    try {
      setIsUpdatingManualWeather(true);

      const manualObject = {
        ...(session.weather_controller_dict?.manual || {}),
        ts: Math.floor(Date.now() / 1000),
        weather_data: filteredRows,
      };

      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...(session.weather_controller_dict || {}),
          manual: manualObject,
        },
      });

      toast.success('Manual weather entry removed');
      // Clear editing state if we removed the row being edited
      if (editingWeatherRow === timeAbs) {
        setEditingWeatherRow(null);
      }
      await invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error removing manual weather data');
    } finally {
      setIsUpdatingManualWeather(false);
    }
  };

  // Handle surface quality change
  const handleSurfaceQualityChange = (surfaceType: SurfaceEnum, quality: number) => {
    const newSurfaceQuality = new SurfaceQuality(surfaceType, quality);
    setSurfaceQualityObj(newSurfaceQuality);
    const currentValue = session?.surface_quality ?? 10.25;
    setSurfaceQualityChanged(newSurfaceQuality.toFloat() !== currentValue);
  };

  // Handle updating surface quality
  const handleUpdateSurfaceQuality = async () => {
    if (!session) {
      toast.error('Session data not available');
      return;
    }

    try {
      setIsUpdatingSurfaceQuality(true);
      await updateSession.mutateAsync({
        surface_quality: surfaceQualityObj.toFloat(),
      });
      toast.success('Surface quality updated');
      setSurfaceQualityChanged(false);
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating surface quality');
    } finally {
      setIsUpdatingSurfaceQuality(false);
    }
  };

  // Use unsaved changes warning hook
  const { clearUnsavedChanges } = useUnsavedChangesWarning({
    enabled: isNew,
    message: 'You have unsaved changes to this session. Are you sure you want to leave?',
    formMethods: formMethods,
  });

  // Used on delete session
  const { open: openDeleteSessionGuard, ActionGuard } = useActionGuard();

  // Update form when session changes
  useEffect(() => {
    if (!isNew && session) {
      formMethods.reset(session);
    }
  }, [session, isNew, formMethods]);

  // Set initial meteorological source from session data
  useEffect(() => {
    if (session?.weather_controller_dict?.selection) {
      // Map backend selection keys to frontend display names
      const selectionMap: Record<string, string> = {
        weather_api: 'Weather Api',
        probe: 'Probe',
        probe_hybrid: 'Probe-API',
        local_station: 'Local Weather Station',
        manual: 'Manual',
        bike_computer: 'Bike Computer',
      };
      const selectedSource = selectionMap[session.weather_controller_dict.selection];
      if (selectedSource) {
        setMeteorologicalSource(selectedSource);
      }
    } else if (session?.meteorological_source) {
      // Fallback to meteorological_source if selection is not available
      setMeteorologicalSource(session.meteorological_source);
    }
  }, [session]);

  // Set initial surface quality from session data
  useEffect(() => {
    if (session?.surface_quality !== undefined && session?.surface_quality !== null) {
      setSurfaceQualityObj(SurfaceQuality.fromFloat(session.surface_quality));
      setSurfaceQualityChanged(false);
    } else {
      // If surface_quality is null, set default and mark as changed so form is dirty
      setSurfaceQualityObj(SurfaceQuality.fromFloat(10.25));
      setSurfaceQualityChanged(true);
    }
  }, [session]);


  // Calculate rider display name
  const getRiderName = (rider_id: string) => (ridersById && ridersById.get(rider_id)?.nickname) || 'None';

  const updateSession = isNew ? useCreateSession() : useUpdateSession(sessionId);

  // Watch for changes to analysis_type or parent_id to show/hide save button
  useEffect(() => {
    if (session) {
      const subscription = formMethods.watch((value, { name }) => {
        if (name === 'analysis_type' || name === 'parent_id') {
          const analysisTypeChanged =
            Number(value.analysis_type) !== session.analysis_type;
          const parentIdChanged = value.parent_id !== session.parent_id;
          setSessionTypeChanged(
            analysisTypeChanged || parentIdChanged,
          );
        }
      });

      return () => subscription.unsubscribe();
    }
  }, [formMethods, session]);

  const redirectToSessions = () => {
    navigate('/sessions');
  };

  const redirectToSession = (session?: any) => {
    navigate(`/sessions/${session.id}`);
  };

  const askToDeleteSession = () => {
    openDeleteSessionGuard({
      message: 'Do you want to delete this session? Data runs will be preserved.',
      actionFn: handleDelete,
    });
  };

  const handleSave = async () => {
    try {
      const formValues = formMethods.getValues();
      const cleanedValues = Object.fromEntries(Object.entries(formValues).filter(([, v]) => v !== ''));

      // Add timezone from browser when creating a new session
      if (isNew) {
        cleanedValues.timezone_initial = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      const updatedSession = await updateSession.mutateAsync(cleanedValues);

      // Clear unsaved changes state after successful save
      clearUnsavedChanges();
      redirectToSession(updatedSession);
    } catch (e) {
      console.error(e);
      toast.error('Unknown error saving session');
    }
  };

  const handleSaveSessionType = async () => {
    try {
      setIsSavingSessionType(true);
      const formValues = formMethods.getValues();

      // Only save analysis_type and parent_id
      const sessionTypeData: any = {
        analysis_type: Number(formValues.analysis_type),
      };

      // Include parent_id if it exists and is not empty
      if (formValues.parent_id && formValues.parent_id !== '') {
        sessionTypeData.parent_id = formValues.parent_id;
      }

      await updateSession.mutateAsync(sessionTypeData);

      setSessionTypeChanged(false);
      toast.success('Session type updated successfully');

      // Invalidate session to refresh data
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating session type');
    } finally {
      setIsSavingSessionType(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSession.mutateAsync(sessionId);
      clearUnsavedChanges();
      toast.success('Session deleted');
      navigate('/sessions');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete session');
    }
  };

  const toggleHidden = async () => {
    try {
      const hidden = !session?.hidden;
      await updateSession.mutateAsync({ hidden });
      await invalidateSession();
      // useUpdateSession does not invalidate the sessions list cache itself
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
      toast.success(hidden ? 'Session hidden' : 'Session unhidden');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update session');
    }
  };

  const DeleteSessionButton = () => {
    return (
      <>
        <Button
          onClick={askToDeleteSession}
          className="cr-bg-red-100 cr-text-red-800 hover:cr-bg-red-200 !cr-normal-case !cr-text-base !cr-px-3 !cr-py-1.5 !cr-border-red-800"
          icon={<HiOutlineTrash className="cr-h-5 cr-w-5" />}
        >
          Delete Session
        </Button>
        <ActionGuard />
      </>
    );
  };

  const HideSessionButton = () => {
    return (
      <Button
        onClick={toggleHidden}
        className="!cr-normal-case !cr-text-base !cr-px-3 !cr-py-1.5"
        icon={
          session?.hidden
            ? <HiOutlineEye className="cr-h-5 cr-w-5" />
            : <HiOutlineEyeSlash className="cr-h-5 cr-w-5" />
        }
      >
        {session?.hidden ? 'Unhide Session' : 'Hide Session'}
      </Button>
    );
  };

  // Check if surface quality option should be shown (only for existing sessions, not during creation)
  const showSurfaceQualityOption =
    !isNew &&
    (currentAnalysisType === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
      currentAnalysisType === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON);

  const AdvancedOptionsMobile = () => {
    if (isNew) return null;

    return (
      <div className="cr-mb-4 cr-mt-8">
        <div
          className="cr-justify-left cr-flex cr-cursor-pointer cr-items-center cr-gap-2 cr-transition-colors cr-duration-200 hover:cr-text-blue-500"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
        >
          <AccordionIcon show={showAdvancedOptions} />
          <h3 className="cr-text-lg cr-font-semibold">Options</h3>
        </div>
        {showAdvancedOptions && (
          <div className="cr-pt-4">
            {/* Add Summary button - only show if summary is null or empty */}
            {(!session?.summary || session.summary.length === 0) && (
              <div className="cr-mb-4">
                <Button
                  onClick={async () => {
                    try {
                      await updateSession.mutateAsync({ summary: 'Text here...' });
                      await invalidateSession();
                      toast.success('Summary added');
                    } catch (e) {
                      console.error(e);
                      toast.error('Failed to add summary');
                    }
                  }}
                  icon={<HiPlus className="cr-h-5 cr-w-5" />}
                  className="!cr-normal-case !cr-text-base !cr-px-3 !cr-py-1.5"
                >
                  Add Summary
                </Button>
              </div>
            )}

            {/* Add Notes button - only show if notes is null or empty */}
            {(!session?.notes || session.notes.length === 0) && (
              <div className="cr-mb-4">
                <Button
                  onClick={async () => {
                    try {
                      await updateSession.mutateAsync({ notes: 'Text here...' });
                      await invalidateSession();
                      toast.success('Notes added');
                    } catch (e) {
                      console.error(e);
                      toast.error('Failed to add notes');
                    }
                  }}
                  icon={<HiPlus className="cr-h-5 cr-w-5" />}
                  className="!cr-normal-case !cr-text-base !cr-px-3 !cr-py-1.5"
                >
                  Add Notes
                </Button>
              </div>
            )}

            {/* Add Unallocated Runs */}
            <div className="cr-mt-6">
              <Button
                onClick={() => {
                  const targetConfigId =
                    session?.run_order?.[0]
                    || session?.configs?.[0]?.id;
                  navigate('/runs', {
                    state: {
                      moveToSessionId: sessionId,
                      moveToConfigId: targetConfigId,
                      moveToSessionDesc:
                        session?.description || 'Session',
                    },
                  });
                }}
                disabled={
                  !session?.run_order?.[0]
                  && !session?.configs?.[0]?.id
                }
                icon={<HiPlus className="cr-h-5 cr-w-5" />}
                className={
                  '!cr-normal-case !cr-text-base '
                  + '!cr-px-3 !cr-py-1.5'
                }
              >
                Add Unallocated Runs
              </Button>
            </div>

            {/* Session Duplicate nested accordion */}
            <div className="cr-mt-6">
              <div
                className={
                  'cr-justify-left cr-flex cr-cursor-pointer '
                  + 'cr-items-center cr-gap-2'
                }
                onClick={() =>
                  setShowSessionDuplicate(!showSessionDuplicate)
                }
              >
                <AccordionIcon show={showSessionDuplicate} />
                <h4
                  className={
                    'cr-text-md cr-font-medium '
                    + 'cr-text-blue-900'
                  }
                >
                  Session Duplicate
                </h4>
              </div>
              {showSessionDuplicate && (
                <div className="cr-pt-4">
                  <div className="cr-flex cr-flex-col cr-gap-3">
                    <Button
                      onClick={async () => {
                        try {
                          const result =
                            await duplicateSessionMutation
                              .mutateAsync({
                                sessionId: sessionId!,
                                mode: 'without_runs',
                              });
                          toast.success(
                            'Session duplicated (without runs)',
                          );
                          navigate(
                            `/sessions/${result.new_session_id}`,
                          );
                        } catch (e) {
                          console.error(e);
                          toast.error(
                            'Failed to duplicate session',
                          );
                        }
                      }}
                      disabled={
                        duplicateSessionMutation.isPending
                      }
                      icon={
                        <HiDocumentDuplicate
                          className="cr-h-5 cr-w-5"
                        />
                      }
                      className={
                        '!cr-normal-case !cr-text-base '
                        + '!cr-px-3 !cr-py-1.5'
                      }
                    >
                      Without Runs
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          const result =
                            await duplicateSessionMutation
                              .mutateAsync({
                                sessionId: sessionId!,
                                mode: 'with_runs',
                              });
                          toast.success(
                            'Session duplicated (with runs)',
                          );
                          navigate(
                            `/sessions/${result.new_session_id}`,
                          );
                        } catch (e) {
                          console.error(e);
                          toast.error(
                            'Failed to duplicate session',
                          );
                        }
                      }}
                      disabled={
                        duplicateSessionMutation.isPending
                      }
                      icon={
                        <HiDocumentDuplicate
                          className="cr-h-5 cr-w-5"
                        />
                      }
                      className={
                        '!cr-normal-case !cr-text-base '
                        + '!cr-px-3 !cr-py-1.5'
                      }
                    >
                      With Runs
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Session Color nested accordion */}
            <div className="cr-mt-6">
              <div
                className="cr-justify-left cr-flex cr-cursor-pointer cr-items-center cr-gap-2"
                onClick={() => setShowSessionColor(!showSessionColor)}
              >
                <AccordionIcon show={showSessionColor} />
                <h4 className="cr-text-md cr-font-medium cr-text-blue-900">Session Color</h4>
              </div>
              {showSessionColor && (
                <div className="cr-pt-4">
                  <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
                    {/* Color Selection */}
                    <div>
                      <label className="cr-mb-2 cr-block cr-text-sm cr-font-medium cr-text-gray-700">
                        Select Color
                      </label>
                      <div className="cr-flex cr-gap-2">
                        {SESSION_COLORS.map((colorOption) => (
                          <button
                            key={colorOption.value || 'none'}
                            type="button"
                            onClick={() => setSessionColor(colorOption.value)}
                            className={`cr-h-8 cr-w-8 cr-rounded-full cr-border-2 cr-transition-all cr-duration-200
                              ${sessionColor === colorOption.value
                                ? 'cr-ring-2 cr-ring-offset-2 cr-ring-blue-900 cr-border-blue-900'
                                : 'cr-border-gray-300 hover:cr-border-gray-400'
                              }
                              ${colorOption.value === null ? 'cr-bg-white' : ''}
                            `}
                            style={{
                              backgroundColor: colorOption.value ? colorOption.hex : undefined,
                            }}
                            title={colorOption.label}
                          >
                            {colorOption.value === null && (
                              <HiOutlineXMark className="cr-h-full cr-w-full cr-p-1 cr-text-gray-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>

            {/* Change Session Type nested accordion */}
            <div className="cr-mt-6">
              <div
                className="cr-justify-left cr-flex cr-cursor-pointer cr-items-center cr-gap-2"
                onClick={() => setShowChangeSessionType(!showChangeSessionType)}
              >
                <AccordionIcon show={showChangeSessionType} />
                <h4 className="cr-text-md cr-font-medium cr-text-blue-900">Change Session Type</h4>
              </div>
              {showChangeSessionType && (
                <div className="cr-pt-4">
                  {/* Session Test Type Selection */}
                  <div className="cr-mb-4 cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-px-4 cr-pb-4 cr-pt-3 cr-shadow-md md:cr-w-[640px]">
                    <FormEntry label="Session Test Type">
                      <FormInput
                        fieldName="analysis_type"
                        required="Session Test Type is Required"
                        options={Object.entries(SessionCatalog)
                          .map(([analysisType, [name, sortOrder, status]]) => ({
                            id: Number(analysisType),
                            name: name as string,
                            sortOrder: sortOrder as number,
                            status: status as SessionStatus,
                            disabled: status === SessionStatus.COMING_SOON || status === SessionStatus.IN_DEVELOPMENT,
                          }))
                          .sort((a, b) => a.sortOrder - b.sortOrder)}
                        optionLabelFn={(option) => {
                          let label = option.name;
                          if (option.status === SessionStatus.BETA) {
                            label += ' - Beta Phase';
                          } else if (option.status === SessionStatus.COMING_SOON) {
                            label += ' - Coming Soon';
                          } else if (option.status === SessionStatus.IN_DEVELOPMENT) {
                            label += ' - Coming Soon';
                          }
                          return label;
                        }}
                        emptyOption="Select Session Test Type"
                        customClass="cr-w-full md:cr-w-[420px]"
                      />
                    </FormEntry>
                  </div>

                  {/* Parent Session Selection for TRAVERSE_TYRE_COMPARISON */}
                  <FormProvider {...formMethods}>
                    <ParentSessionSelector
                      showDropdown={showParentSessionDropdown}
                      className="cr-mb-4"
                      currentSessionId={sessionId}
                    />
                  </FormProvider>

                  {/* Velodrome Route Selector */}
                  {isVelodromeSession && (
                    <VelodromeRouteSelector
                      analysisType={currentAnalysisType}
                      selectedRouteId={session?.route_id}
                      onRouteSelect={async (routeId) => {
                        try {
                          await updateSession.mutateAsync({ route_id: routeId });
                          toast.success(routeId ? 'Velodrome route selected' : 'Velodrome route deselected');
                          await invalidateSession();
                        } catch (e) {
                          console.error(e);
                          toast.error('Failed to update velodrome route');
                        }
                      }}
                      className="cr-mb-4"
                    />
                  )}

                  {/* Save Button */}
                  {sessionTypeChanged && (
                    <div className="cr-flex cr-justify-end">
                      <Button
                        onClick={handleSaveSessionType}
                        disabled={isSavingSessionType}
                        icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5" />}
                      >
                        {isSavingSessionType ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Meteorological Source nested accordion */}
            <div className="cr-mt-6">
              <div
                className="cr-justify-left cr-flex cr-cursor-pointer cr-items-center cr-gap-2"
                onClick={() => setShowMeteorologicalSource(!showMeteorologicalSource)}
              >
                <AccordionIcon show={showMeteorologicalSource} />
                <h4 className="cr-text-md cr-font-medium cr-text-blue-900">Meteorological Source</h4>
              </div>
              {showMeteorologicalSource && (
                <div className="cr-pt-4">
                  {/* Meteorological Source Selection with Plot */}
                  <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md lg:cr-flex-row">
                    {/* Temperature Plot - Left Side */}
                    <div className="cr-flex cr-flex-1 cr-flex-col cr-gap-4">
                      <WeatherTemperaturePlot
                        weatherControllerDict={session?.weather_controller_dict || null}
                        selectedSource={meteorologicalSource}
                        utcOffsetSeconds={Number(session?.utc_offset ?? 0)}
                      />
                      <div className="cr-flex cr-flex-wrap cr-items-end cr-justify-between cr-gap-2">
                        <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
                          <label
                            htmlFor={meteorologicalSourceSelectId}
                            className="cr-text-sm cr-font-medium cr-text-gray-700"
                          >
                            Select Source
                          </label>
                          <select
                            id={meteorologicalSourceSelectId}
                            className="cr-h-11 cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-text-base focus:cr-border-blue-500 focus:cr-outline-none"
                            value={meteorologicalSource}
                            onChange={(e) => handleMeteorologicalSourceChange(e.target.value)}
                          >
                            <option value="Weather Api">Weather Api</option>
                            <option value="Probe">Probe</option>
                            <option value="Probe-API">Probe-API</option>
                            <option value="Local Weather Station">Local Weather Station</option>
                            <option value="Manual">Manual</option>
                            {showBikeComputerOption && <option value="Bike Computer">Bike Computer</option>}
                          </select>
                        </div>
                        {meteorologicalSourceChanged && (
                          <Button
                            onClick={handleUpdateWeatherSource}
                            disabled={isUpdatingWeatherSource}
                            icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5" />}
                            className="cr-h-11"
                          >
                            {isUpdatingWeatherSource ? 'Saving...' : 'SAVE'}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Radio Button Selection - Right Side */}
                    <div className="cr-flex cr-flex-col cr-justify-center lg:cr-w-64 lg:cr-border-l lg:cr-border-gray-200 lg:cr-pl-4">
                      <FormEntry label="Select Source">
                        <div className="cr-flex cr-flex-col cr-gap-3">
                          <div className="cr-flex cr-items-center cr-justify-between">
                            <div className="cr-flex cr-flex-1 cr-items-center">
                              <span
                                className="cr-mr-2 cr-inline-block cr-h-3 cr-w-3 cr-flex-shrink-0 cr-rounded-full"
                                style={{ backgroundColor: '#3b82f6' }}
                              />
                              <span>Weather Api</span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedWeatherEdit(expandedWeatherEdit === 'Weather Api' ? null : 'Weather Api')
                              }
                              className="cr-ml-2 cr-text-gray-600 hover:cr-text-gray-900"
                            >
                              <HiOutlinePencil className="cr-h-4 cr-w-4" />
                            </button>
                          </div>
                          <div className="cr-flex cr-items-center cr-justify-between">
                            <div className="cr-flex cr-flex-1 cr-items-center">
                              <span
                                className="cr-mr-2 cr-inline-block cr-h-3 cr-w-3 cr-flex-shrink-0 cr-rounded-full"
                                style={{ backgroundColor: '#10b981' }}
                              />
                              <span>Probe</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setExpandedWeatherEdit(expandedWeatherEdit === 'Probe' ? null : 'Probe')}
                              className="cr-ml-2 cr-text-gray-600 hover:cr-text-gray-900"
                            >
                              <HiOutlinePencil className="cr-h-4 cr-w-4" />
                            </button>
                          </div>
                          <div className="cr-flex cr-items-center cr-justify-between">
                            <div className="cr-flex cr-flex-1 cr-items-center">
                              <span
                                className="cr-mr-2 cr-inline-block cr-h-3 cr-w-3 cr-flex-shrink-0 cr-rounded-full"
                                style={{ backgroundColor: '#06b6d4' }}
                              />
                              <span>Probe-API</span>
                            </div>
                          </div>
                          <div className="cr-flex cr-items-center cr-justify-between">
                            <div className="cr-flex cr-flex-1 cr-items-center">
                              <span
                                className="cr-mr-2 cr-inline-block cr-h-3 cr-w-3 cr-flex-shrink-0 cr-rounded-full"
                                style={{ backgroundColor: '#f59e0b' }}
                              />
                              <span>Local Weather Station</span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedWeatherEdit(
                                  expandedWeatherEdit === 'Local Weather Station' ? null : 'Local Weather Station',
                                )
                              }
                              className="cr-ml-2 cr-text-gray-600 hover:cr-text-gray-900"
                            >
                              <HiOutlinePencil className="cr-h-4 cr-w-4" />
                            </button>
                          </div>
                          <div className="cr-flex cr-items-center cr-justify-between">
                            <div className="cr-flex cr-flex-1 cr-items-center">
                              <span
                                className="cr-mr-2 cr-inline-block cr-h-3 cr-w-3 cr-flex-shrink-0 cr-rounded-full"
                                style={{ backgroundColor: '#8b5cf6' }}
                              />
                              <span>Manual</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setExpandedWeatherEdit(expandedWeatherEdit === 'Manual' ? null : 'Manual')}
                              className="cr-ml-2 cr-text-gray-600 hover:cr-text-gray-900"
                            >
                              <HiOutlinePencil className="cr-h-4 cr-w-4" />
                            </button>
                          </div>
                          {showBikeComputerOption && (
                            <div className="cr-flex cr-items-center cr-justify-between">
                              <div className="cr-flex cr-flex-1 cr-items-center">
                                <span
                                  className="cr-mr-2 cr-inline-block cr-h-3 cr-w-3 cr-flex-shrink-0 cr-rounded-full"
                                  style={{ backgroundColor: '#4b5563' }}
                                />
                                <span>Bike Computer</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </FormEntry>
                    </div>
                  </div>

                  {/* Expandable Edit Cards */}
                  {expandedWeatherEdit === 'Weather Api' && (
                    <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
                      <h5 className="cr-mb-3 cr-font-semibold cr-text-gray-800">Update Weather Api Data</h5>
                      <p className="cr-mb-3 cr-text-sm cr-text-gray-600">
                        Fetch the latest weather data from the Weather API service.
                      </p>
                      <Button
                        onClick={handleUpdateWeatherApi}
                        icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5" />}
                      >
                        Update Weather Api
                      </Button>
                    </div>
                  )}
                  {expandedWeatherEdit === 'Probe' && (
                    <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
                      <h5 className="cr-mb-3 cr-font-semibold cr-text-gray-800">Probe Temperature Offset</h5>
                      <p className="cr-mb-3 cr-text-sm cr-text-gray-600">
                        Adjust the temperature offset for the probe sensor.
                      </p>
                      <form onSubmit={handleUpdateProbeOffset}>
                        <div className="cr-mb-4 cr-flex cr-items-center cr-gap-3">
                          <label htmlFor="probeOffset" className="cr-text-sm cr-font-medium cr-text-gray-700">
                            Temperature Offset (°C):
                          </label>
                          <input
                            id="probeOffset"
                            name="probeOffset"
                            type="text"
                            inputMode="decimal"
                            defaultValue={sessionProbeOffset ?? 0}
                            placeholder="e.g. 0.5"
                            className="cr-w-24 cr-rounded cr-border cr-border-gray-300 cr-px-3 cr-py-2 focus:cr-border-blue-500 focus:cr-outline-none"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={isUpdatingProbeOffset}
                          className="cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-px-4 cr-py-2 cr-text-sm cr-uppercase cr-text-blue-950 cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50 hover:cr-text-blue-700 disabled:cr-bg-cirrusBlue-100 disabled:cr-text-blue-950 disabled:cr-opacity-50"
                        >
                          {isUpdatingProbeOffset ? 'Updating...' : 'Update Offset'}
                          <HiOutlineCheckCircle className="cr-h-5 cr-w-5" />
                        </button>
                      </form>
                    </div>
                  )}
                  {expandedWeatherEdit === 'Local Weather Station' && (
                    <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
                      <h5 className="cr-mb-3 cr-font-semibold cr-text-gray-800">Local Weather Station Settings</h5>
                      <p className="cr-text-sm cr-text-gray-600">Configure local weather station data source.</p>
                    </div>
                  )}
                  {expandedWeatherEdit === 'Manual' && (
                    <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
                      <div className="cr-mb-3 cr-flex cr-items-center cr-gap-2">
                        <h5 className="cr-font-semibold cr-text-gray-800">Manual Weather Entry</h5>
                        {isUpdatingManualWeather && (
                          <ImSpinner2 className="cr-h-5 cr-w-5 cr-animate-spin cr-text-blue-600" />
                        )}
                      </div>
                      <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
                        Manually enter weather data for a specific time.
                      </p>
                      <div className="cr-mb-4">
                        {manualWeatherRows.length > 0 || editingWeatherRow === -1 ? (
                          <div className="cr-overflow-x-auto">
                            <table className="cr-min-w-full cr-text-sm">
                              <thead>
                                <tr className="cr-text-left cr-text-xs cr-font-semibold cr-uppercase cr-text-gray-500">
                                  <th className="cr-py-2 cr-pr-4">Time</th>
                                  <th className="cr-py-2 cr-pr-4">Temperature</th>
                                  <th className="cr-py-2 cr-pr-4">Humidity</th>
                                  <th className="cr-py-2">Action</th>
                                </tr>
                              </thead>
                              <tbody className="cr-divide-y cr-divide-gray-200">
                                {manualWeatherRows.map((row: any, index: number) => {
                                  const tempValue =
                                    typeof row?.Temp_T === 'number'
                                      ? row.Temp_T
                                      : row?.Temp_T != null && !Number.isNaN(Number(row.Temp_T))
                                        ? Number(row.Temp_T)
                                        : null;
                                  const humidityValue =
                                    typeof row?.Humidity_x === 'number'
                                      ? row.Humidity_x
                                      : row?.Humidity_x != null && !Number.isNaN(Number(row.Humidity_x))
                                        ? Number(row.Humidity_x)
                                        : null;
                                  const timeValue = Number(row?.TimeAbs_t);
                                  const hasValidTime = !Number.isNaN(timeValue);
                                  // Render TimeAbs_t (a UTC epoch) at the recording-location wall
                                  // clock, matching the weather plots (shift by utc_offset, then read
                                  // UTC parts via toISOString) so the viewer's browser zone stays out.
                                  const utcOffsetSeconds = Number(session?.utc_offset ?? 0);
                                  const recordingDate = hasValidTime
                                    ? new Date((timeValue + utcOffsetSeconds) * 1000)
                                    : null;
                                  const dateLabel = recordingDate
                                    ? recordingDate.toISOString().slice(0, 16).replace('T', ' ')
                                    : '—';
                                  const tempLabel = tempValue != null ? `${tempValue.toFixed(1)} °C` : '—';
                                  const humidityLabel = humidityValue != null ? humidityValue.toFixed(2) : '—';
                                  const key = `${row?.TimeAbs_t ?? 'row'}-${index}`;
                                  const isEditing = editingWeatherRow === row?.TimeAbs_t;

                                  if (isEditing) {
                                    // Editing mode - show inline form
                                    const hour = recordingDate ? recordingDate.getUTCHours() : 12;
                                    const minute = recordingDate ? recordingDate.getUTCMinutes() : 0;
                                    return (
                                      <tr key={key} className="cr-bg-blue-50">
                                        <td className="cr-py-2 cr-pr-4" colSpan={4}>
                                          <form
                                            onSubmit={(e) => {
                                              e.preventDefault();
                                              const formData = new FormData(e.currentTarget);
                                              const hourStr = ((formData.get('hour') as string) || '').trim();
                                              const minuteStr = ((formData.get('minute') as string) || '').trim();
                                              const tempStr = ((formData.get('temperature') as string) || '').trim();
                                              const humidityStr = ((formData.get('humidity') as string) || '').trim();

                                              // Validation
                                              if (
                                                hourStr === '' ||
                                                minuteStr === '' ||
                                                tempStr === '' ||
                                                humidityStr === ''
                                              ) {
                                                toast.error('All fields are required');
                                                return;
                                              }

                                              const hourVal = Number(hourStr);
                                              const minuteVal = Number(minuteStr);
                                              const tempVal = Number(tempStr);
                                              const humidityVal = Number(humidityStr);

                                              if (Number.isNaN(hourVal) || hourVal < 0 || hourVal > 23) {
                                                toast.error('Hour must be between 0 and 23');
                                                return;
                                              }
                                              if (Number.isNaN(minuteVal) || minuteVal < 0 || minuteVal > 59) {
                                                toast.error('Minute must be between 0 and 59');
                                                return;
                                              }
                                              if (Number.isNaN(tempVal)) {
                                                toast.error('Temperature must be a valid number');
                                                return;
                                              }
                                              if (Number.isNaN(humidityVal) || humidityVal < 0 || humidityVal > 1) {
                                                toast.error('Humidity must be between 0 and 1');
                                                return;
                                              }

                                              handleSaveManualWeather(hourVal, minuteVal, tempVal, humidityVal);
                                            }}
                                            className="cr-flex cr-flex-wrap cr-items-end cr-gap-2"
                                          >
                                            <div className="cr-flex cr-flex-col cr-gap-1">
                                              <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                                Hour (0-23):
                                              </label>
                                              <input
                                                name="hour"
                                                type="text"
                                                inputMode="numeric"
                                                defaultValue={hour}
                                                className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                              />
                                            </div>
                                            <div className="cr-flex cr-flex-col cr-gap-1">
                                              <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                                Minute (0-59):
                                              </label>
                                              <input
                                                name="minute"
                                                type="text"
                                                inputMode="numeric"
                                                defaultValue={minute}
                                                className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                              />
                                            </div>
                                            <div className="cr-flex cr-flex-col cr-gap-1">
                                              <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                                Temp (°C):
                                              </label>
                                              <input
                                                name="temperature"
                                                type="text"
                                                inputMode="decimal"
                                                defaultValue={tempValue ?? 20}
                                                className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                              />
                                            </div>
                                            <div className="cr-flex cr-flex-col cr-gap-1">
                                              <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                                Humidity (0-1):
                                              </label>
                                              <input
                                                name="humidity"
                                                type="text"
                                                inputMode="decimal"
                                                defaultValue={humidityValue ?? 0.5}
                                                className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                              />
                                            </div>
                                            <div className="cr-flex cr-items-center cr-gap-2">
                                              <button
                                                type="submit"
                                                disabled={isUpdatingManualWeather}
                                                className="cr-rounded cr-bg-blue-600 cr-px-3 cr-py-1 cr-text-sm cr-text-white hover:cr-bg-blue-700 disabled:cr-opacity-50"
                                                title="Save"
                                              >
                                                <HiOutlineCheckCircle className="cr-h-5 cr-w-5" />
                                              </button>
                                              <button
                                                type="button"
                                                onClick={handleCancelEdit}
                                                disabled={isUpdatingManualWeather}
                                                className="cr-rounded cr-bg-gray-600 cr-px-3 cr-py-1 cr-text-sm cr-text-white hover:cr-bg-gray-700 disabled:cr-opacity-50"
                                                title="Cancel"
                                              >
                                                <HiOutlineXMark className="cr-h-5 cr-w-5" />
                                              </button>
                                            </div>
                                          </form>
                                        </td>
                                      </tr>
                                    );
                                  }

                                  // View mode - show data
                                  return (
                                    <tr key={key} className="cr-text-gray-700">
                                      <td className="cr-py-2 cr-pr-4">{dateLabel}</td>
                                      <td className="cr-py-2 cr-pr-4">{tempLabel}</td>
                                      <td className="cr-py-2 cr-pr-4">{humidityLabel}</td>
                                      <td className="cr-py-2">
                                        <div className="cr-flex cr-items-center cr-gap-2">
                                          <button
                                            type="button"
                                            className="cr-text-gray-600 hover:cr-text-gray-900 disabled:cr-cursor-not-allowed disabled:cr-opacity-60"
                                            onClick={() => handleEditManualWeatherRow(row)}
                                            disabled={isUpdatingManualWeather || editingWeatherRow !== null}
                                            title="Edit"
                                          >
                                            <HiOutlinePencil className="cr-h-4 cr-w-4" />
                                          </button>
                                          <button
                                            type="button"
                                            className="cr-text-red-600 hover:cr-text-red-800 disabled:cr-cursor-not-allowed disabled:cr-opacity-60"
                                            onClick={() => handleRemoveManualWeatherRow(row?.TimeAbs_t)}
                                            disabled={isUpdatingManualWeather || editingWeatherRow !== null}
                                            title="Remove"
                                          >
                                            <HiOutlineTrash className="cr-h-4 cr-w-4" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {/* New row form when adding */}
                                {editingWeatherRow === -1 && (
                                  <tr className="cr-bg-blue-50">
                                    <td className="cr-py-2 cr-pr-4" colSpan={4}>
                                      <form
                                        onSubmit={(e) => {
                                          e.preventDefault();
                                          const formData = new FormData(e.currentTarget);
                                          const hourStr = ((formData.get('hour') as string) || '').trim();
                                          const minuteStr = ((formData.get('minute') as string) || '').trim();
                                          const tempStr = ((formData.get('temperature') as string) || '').trim();
                                          const humidityStr = ((formData.get('humidity') as string) || '').trim();

                                          // Validation
                                          if (
                                            hourStr === '' ||
                                            minuteStr === '' ||
                                            tempStr === '' ||
                                            humidityStr === ''
                                          ) {
                                            toast.error('All fields are required');
                                            return;
                                          }

                                          const hourVal = Number(hourStr);
                                          const minuteVal = Number(minuteStr);
                                          const tempVal = Number(tempStr);
                                          const humidityVal = Number(humidityStr);

                                          if (Number.isNaN(hourVal) || hourVal < 0 || hourVal > 23) {
                                            toast.error('Hour must be between 0 and 23');
                                            return;
                                          }
                                          if (Number.isNaN(minuteVal) || minuteVal < 0 || minuteVal > 59) {
                                            toast.error('Minute must be between 0 and 59');
                                            return;
                                          }
                                          if (Number.isNaN(tempVal)) {
                                            toast.error('Temperature must be a valid number');
                                            return;
                                          }
                                          if (Number.isNaN(humidityVal) || humidityVal < 0 || humidityVal > 1) {
                                            toast.error('Humidity must be between 0 and 1');
                                            return;
                                          }

                                          handleSaveManualWeather(hourVal, minuteVal, tempVal, humidityVal);
                                        }}
                                        className="cr-flex cr-flex-wrap cr-items-end cr-gap-2"
                                      >
                                        <div className="cr-flex cr-flex-col cr-gap-1">
                                          <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                            Hour (0-23):
                                          </label>
                                          <input
                                            name="hour"
                                            type="text"
                                            inputMode="numeric"
                                            defaultValue="12"
                                            className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                          />
                                        </div>
                                        <div className="cr-flex cr-flex-col cr-gap-1">
                                          <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                            Minute (0-59):
                                          </label>
                                          <input
                                            name="minute"
                                            type="text"
                                            inputMode="numeric"
                                            defaultValue="0"
                                            className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                          />
                                        </div>
                                        <div className="cr-flex cr-flex-col cr-gap-1">
                                          <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                            Temp (°C):
                                          </label>
                                          <input
                                            name="temperature"
                                            type="text"
                                            inputMode="decimal"
                                            defaultValue="20"
                                            className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                          />
                                        </div>
                                        <div className="cr-flex cr-flex-col cr-gap-1">
                                          <label className="cr-text-xs cr-font-medium cr-text-gray-700">
                                            Humidity (0-1):
                                          </label>
                                          <input
                                            name="humidity"
                                            type="text"
                                            inputMode="decimal"
                                            defaultValue="0.5"
                                            className="cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm focus:cr-border-blue-500 focus:cr-outline-none"
                                          />
                                        </div>
                                        <div className="cr-flex cr-items-center cr-gap-2">
                                          <button
                                            type="submit"
                                            disabled={isUpdatingManualWeather}
                                            className="cr-rounded cr-bg-blue-600 cr-px-3 cr-py-1 cr-text-sm cr-text-white hover:cr-bg-blue-700 disabled:cr-opacity-50"
                                            title="Save"
                                          >
                                            <HiOutlineCheckCircle className="cr-h-5 cr-w-5" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={handleCancelEdit}
                                            disabled={isUpdatingManualWeather}
                                            className="cr-rounded cr-bg-gray-600 cr-px-3 cr-py-1 cr-text-sm cr-text-white hover:cr-bg-gray-700 disabled:cr-opacity-50"
                                            title="Cancel"
                                          >
                                            <HiOutlineXMark className="cr-h-5 cr-w-5" />
                                          </button>
                                        </div>
                                      </form>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="cr-text-sm cr-text-gray-500">No manual weather entries yet.</p>
                        )}
                      </div>
                      {/* Add button - only show when not editing */}
                      {editingWeatherRow === null && (
                        <div className="cr-flex cr-justify-end">
                          <button
                            type="button"
                            onClick={handleAddNewRow}
                            disabled={isUpdatingManualWeather}
                            className="cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-p-2 cr-text-sm cr-text-gray-600 hover:cr-bg-gray-100 hover:cr-text-gray-900 disabled:cr-opacity-50"
                            title="Add Manual Entry"
                          >
                            <HiPlus className="cr-h-4 cr-w-4" />
                            <span>ENTRY</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Surface Quality nested accordion - only for tyre tests */}
            {showSurfaceQualityOption && (
              <div className="cr-mt-6">
                <div
                  className="cr-justify-left cr-flex cr-cursor-pointer cr-items-center cr-gap-2"
                  onClick={() => setShowSurfaceQuality(!showSurfaceQuality)}
                >
                  <AccordionIcon show={showSurfaceQuality} />
                  <h4 className="cr-text-md cr-font-medium cr-text-blue-900">Surface Quality</h4>
                </div>
                {showSurfaceQuality && (
                  <div className="cr-pt-4">
                    <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md lg:cr-flex-row">
                      {/* Left Side - Controls */}
                      <div className="cr-flex-1 cr-space-y-4">
                        {/* Surface Type Dropdown */}
                        <div>
                          <label
                            htmlFor="surface-type-select"
                            className="cr-mb-2 cr-block cr-text-sm cr-font-medium cr-text-gray-700"
                          >
                            Surface Type
                          </label>
                          <select
                            id="surface-type-select"
                            className="cr-h-11 cr-w-full cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-text-base focus:cr-border-blue-500 focus:cr-outline-none"
                            value={surfaceQualityObj.surface_type}
                            onChange={(e) =>
                              handleSurfaceQualityChange(
                                Number(e.target.value) as SurfaceEnum,
                                surfaceQualityObj.quality,
                              )
                            }
                          >
                            <option value={SurfaceEnum.VELODROME}>Velodrome</option>
                            <option value={SurfaceEnum.PAVEMENT}>Pavement</option>
                            <option value={SurfaceEnum.COBBLES}>Cobbles</option>
                            <option value={SurfaceEnum.GRAVEL}>Gravel</option>
                            <option value={SurfaceEnum.DIRT}>Dirt</option>
                          </select>
                        </div>

                        {/* Quality Input */}
                        <div>
                          <label
                            htmlFor="quality-input"
                            className="cr-mb-2 cr-block cr-text-sm cr-font-medium cr-text-gray-700"
                          >
                            Quality (0.0 - 1.0)
                          </label>
                          <div className="cr-flex cr-items-center cr-gap-3">
                            <input
                              id="quality-input"
                              type="number"
                              step="0.01"
                              min="0"
                              max="1"
                              className="cr-h-11 cr-w-32 cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-text-base focus:cr-border-blue-500 focus:cr-outline-none"
                              value={surfaceQualityObj.quality}
                              onChange={(e) => {
                                const newQuality = parseFloat(e.target.value);
                                if (!isNaN(newQuality) && newQuality >= 0 && newQuality <= 1) {
                                  handleSurfaceQualityChange(surfaceQualityObj.surface_type, newQuality);
                                }
                              }}
                            />
                            <span className="cr-text-sm cr-text-gray-600">0.0 = very smooth, 0.5+ = very rough</span>
                          </div>
                        </div>

                        {/* Current Description */}
                        <div className="cr-rounded cr-bg-neutral-100 cr-p-3">
                          <p className="cr-text-sm cr-font-medium cr-text-blue-900">
                            {surfaceQualityObj.getDescription()}
                          </p>
                        </div>

                        {/* Save Button */}
                        {surfaceQualityChanged && (
                          <Button
                            onClick={handleUpdateSurfaceQuality}
                            disabled={isUpdatingSurfaceQuality}
                            icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5 cr-stroke-[2.2px]" />}
                            className="cr-h-11 !cr-text-base !cr-font-semibold !cr-normal-case"
                          >
                            {isUpdatingSurfaceQuality ? 'Saving...' : 'Save'}
                          </Button>
                        )}
                      </div>

                      {/* Right Side - Guide */}
                      <div className="lg:cr-w-96 lg:cr-border-l lg:cr-border-gray-200 lg:cr-pl-4">
                        <h5 className="cr-mb-3 cr-text-sm cr-font-semibold cr-text-gray-800">Surface Quality Guide</h5>
                        <div className="cr-space-y-2 cr-text-xs cr-text-gray-600">
                          <div className="cr-rounded cr-bg-gray-50 cr-p-2">
                            <p className="cr-mb-1 cr-font-semibold">Pavement - Bicycle Roughness Index (BRI)</p>
                            <div className="cr-space-y-1 cr-pl-2">
                              <p>
                                <span className="cr-font-semibold">Very Smooth (0.0)</span> - Recently paved, smooth
                                blacktop, or high-quality concrete bike paths. Minimal to no perceptible vibration
                                through the handlebars.
                              </p>
                              <p>
                                <span className="cr-font-semibold">Smooth (0.2)</span> - Standard asphalt in good
                                condition. You can feel a slight texture, but not uncomfortable.
                              </p>
                              <p>
                                <span className="cr-font-semibold">Fair (0.3)</span> - Older asphalt with some cracking
                                and surface wear. A noticeable and constant &ldquo;buzz&rdquo; or vibration is felt.
                              </p>
                              <p>
                                <span className="cr-font-semibold">Rough (0.4)</span> - Coarse chip seal, or pavement
                                with frequent cracks and minor potholes. The ride can be uncomfortable over longer
                                distances.
                              </p>
                              <p>
                                <span className="cr-font-semibold">Very Rough (0.5+)</span> - Significantly deteriorated
                                pavement with potholes and large cracks that require the rider to pay close attention to
                                their line choice. Uncomfortable for most riding.
                              </p>
                            </div>
                          </div>
                          <div className="cr-rounded cr-bg-gray-50 cr-p-2">
                            <p className="cr-text-xs cr-italic">
                              For non-pavement surfaces, quality is subjective. Higher resolution values (e.g., 0.24)
                              can be used to differentiate small differences.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Hide / Delete Session Buttons */}
            <div className="cr-mt-6">
              <ButtonList>
                <HideSessionButton />
                <DeleteSessionButton />
              </ButtonList>
            </div>
          </div>
        )}
      </div>
    );
  };

  const SessionNavigation = () => {
    if (isNew || (!previousSessionId && !nextSessionId)) {
      return null;
    }

    // Extract current tab from URL path
    const pathParts = location.pathname.split('/');
    const currentTab = pathParts[3] || 'config'; // Default to 'config' if no tab specified
    const validTabs = ['config', 'results', 'charts', 'tyre-plots'];

    // Helper to check if a session supports tyre-plots tab
    const sessionSupportsTyrePlots = (targetSessionId: string) => {
      const targetSession = allSessions?.find((s) => s.id === targetSessionId);
      return (
        targetSession?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
        targetSession?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON
      );
    };

    // Helper to get the navigation path for a target session
    const getNavigationPath = (targetSessionId: string) => {
      let tab = currentTab;
      // If current tab is tyre-plots but target doesn't support it, fall back to config
      if (tab === 'tyre-plots' && !sessionSupportsTyrePlots(targetSessionId)) {
        tab = 'config';
      }
      // Only append tab if it's a valid tab
      if (validTabs.includes(tab)) {
        return `/sessions/${targetSessionId}/${tab}`;
      }
      return `/sessions/${targetSessionId}/config`;
    };

    return (
      <div className="cr-inline-flex cr-flex-col cr-items-center cr-gap-1.5">
        <div className="cr-text-sm cr-font-medium cr-text-blue-900 cr-font-main">Sessions</div>
        <div className="cr-flex cr-gap-2">
          <div className="cr-flex cr-flex-col cr-items-center cr-gap-0.5">
            <button
              onClick={() => previousSessionId && navigate(getNavigationPath(previousSessionId))}
              disabled={!previousSessionId}
              className="cr-flex cr-items-center cr-justify-center cr-rounded-md cr-bg-gray-200
                cr-p-2 cr-text-blue-900 cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50
                disabled:cr-cursor-not-allowed disabled:cr-opacity-40"
              title="Previous Session"
            >
              <HiChevronLeft className="cr-h-5 cr-w-5" />
            </button>
            <div className="cr-text-[13px] cr-text-blue-900 cr-font-main">Prev</div>
          </div>
          <div className="cr-flex cr-flex-col cr-items-center cr-gap-0.5">
            <button
              onClick={() => nextSessionId && navigate(getNavigationPath(nextSessionId))}
              disabled={!nextSessionId}
              className="cr-flex cr-items-center cr-justify-center cr-rounded-md cr-bg-gray-200
                cr-p-2 cr-text-blue-900 cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50
                disabled:cr-cursor-not-allowed disabled:cr-opacity-40"
              title="Next Session"
            >
              <HiChevronRight className="cr-h-5 cr-w-5" />
            </button>
            <div className="cr-text-[13px] cr-text-blue-900 cr-font-main">Next</div>
          </div>
        </div>
      </div>
    );
  };

  const SessionModeToggle = () => {
    if (!isNew || !isMissingConfigs) return null;
    return (
      <div className={
        'cr-mb-4 cr-flex cr-flex-col cr-rounded-lg '
        + 'cr-bg-white cr-p-2 cr-text-center cr-shadow-md '
        + 'md:cr-w-[640px] md:cr-text-left'
      }>
        <FormEntry label="Options">
          <div className="cr-flex cr-flex-row cr-gap-4">
            <label className={
              'cr-flex cr-cursor-pointer cr-items-center'
            }>
              <input
                type="radio"
                name="sessionMode"
                value="basic"
                checked={sessionMode === 'basic'}
                onChange={() => setSessionMode('basic')}
                className="cr-mr-2"
              />
              <span>Basic</span>
            </label>
            <label className={
              'cr-flex cr-cursor-pointer cr-items-center'
            }>
              <input
                type="radio"
                name="sessionMode"
                value="advanced"
                checked={sessionMode === 'advanced'}
                onChange={() => setSessionMode('advanced')}
                className="cr-mr-2"
              />
              <span>Advanced</span>
            </label>
          </div>
        </FormEntry>
      </div>
    );
  };

  const ButtonActions = () => {
    if (isNew) {
      return (
        <>
          <ButtonList>
            <Button
              onClick={formMethods.handleSubmit(handleSave)}
              icon={<HiOutlineCheckCircle className={'cr-size-7'} />}
              disabled={updateSession.isPending || showRiderWarning || showTestTypeWarning || showVelodromeWarning}
            >
              Save
            </Button>
            <Button
              onClick={redirectToSessions}
              icon={<HiOutlineXMark className={'cr-size-7'} />}
              disabled={updateSession.isPending}
            >
              Cancel
            </Button>
          </ButtonList>
          {/* Explain why Save is disabled for a velodrome session with no route chosen yet. */}
          {showVelodromeWarning && (
            <div className="cr-mt-2 cr-flex cr-items-center cr-gap-1.5 cr-text-sm cr-text-orange-600">
              <HiOutlineExclamationTriangle className="cr-h-4 cr-w-4 cr-flex-shrink-0" />
              <span>Select or create a velodrome above to continue.</span>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <FormProvider {...formMethods}>
      <div className="cr-w-full">
        {/* Header Row: Header + Underline + Back Link */}
        <SectionHeader
          title={isNew ? 'New Session' : 'Session Details'}
          isLoading={isSessionFetching || updateSession.isPending}
          backLink={headerBackLink}
          className="!cr-mb-6"
         
        />
        <div>
          <Skeleton isPending={isSessionPending || isRidersPending}>
            <ButtonActions />
            <div className="cr-flex cr-items-start cr-gap-4">
              <div className="cr-flex-1">
                <SessionCards
                  formMethods={formMethods}
                  ridersById={ridersById}
                  getRiderName={getRiderName}
                  isNew={isNew}
                  updateSession={updateSession}
                  showRiderWarning={showRiderWarning}
                  AdvancedOptionsComponent={AdvancedOptionsMobile}
                  session={session}
                  onSummaryUpdate={invalidateSession}
                />
              </div>
              <SessionNavigation />
            </div>
            {!isSessionFetching && isMissingConfigs && (
              <SessionModeToggle />
            )}
            {!isSessionFetching && isMissingConfigs
              && sessionMode === 'advanced' && (
              <NewSessionCalibrationParameters
                onTestTypeWarningChange={
                  setShowTestTypeWarning
                }
                onVelodromeWarningChange={
                  setShowVelodromeWarning
                }
              />
            )}
            {!isSessionFetching && isMissingConfigs && (
              <div className={
                sessionMode === 'basic'
                  ? 'cr-pb-48 md:cr-pb-0'
                  : 'cr-pb-96 md:cr-pb-0'
              } />
            )}
            {!isSessionFetching && isMissingConfigs && (
              <div className={
                'cr-fixed cr-bottom-0 cr-left-0 '
                + 'cr-right-0 cr-z-50 cr-max-h-[50vh] '
                + 'cr-overflow-y-auto cr-border-t '
                + 'cr-border-gray-200 cr-bg-gray-50 '
                + 'cr-px-4 cr-pb-4 cr-pt-4 '
                + 'md:cr-relative md:cr-max-h-none '
                + 'md:cr-border-0 md:cr-bg-transparent '
                + 'md:cr-p-0'
              }>
                <NewSessionCalibrationConfig
                  ridersById={ridersById || new Map()}
                  onRiderWarningChange={
                    setShowRiderWarning
                  }
                  basicMode={sessionMode === 'basic'}
                />
              </div>
            )}
            {!isSessionFetching && isMissingConfigs
              && sessionMode === 'advanced' && !isVelodromeSession && (
              <NewSessionRouteSelector />
            )}
            {!isSessionFetching && isMissingConfigs && (
              <ButtonActions />
            )}
          </Skeleton>
        </div>
      </div>
    </FormProvider>
  );
}
